import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/common/PageHero";
import { api, formatApiError } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { EXPERIENCES } from "../lib/data";

const EVENT_TYPES = EXPERIENCES.map((e) => e.title);
const STEPS = ["The Event", "The Treats", "You", "Review"];

const inputCls = "w-full rounded-2xl bg-white border border-zinc-300 px-5 py-4 text-sm font-medium outline-none focus:ring-2 focus:ring-pink placeholder:text-zinc-400";
const labelCls = "block text-[11px] font-bold tracking-[0.25em] uppercase text-zinc-500 mb-2";

const INITIAL = {
    event_type: EVENT_TYPES[0], event_date: "", start_time: "", duration: "", guests: "",
    address: "", setting: "indoor", treats: "both", package_choice: "", add_ons: "",
    flavor_cotton_candy: [], flavor_popcorn: [],
    name: "", email: "", phone: "", company: "", notes: "",
};

const formatEventDateTime = (date, time) => {
    if (!date) return "—";
    try {
        const d = new Date(`${date}T${time || "00:00"}`);
        const datePart = d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
        return time ? `${datePart} at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : datePart;
    } catch {
        return `${date} at ${time}`;
    }
};

const FlavorChips = ({ productType, label, flavors, selected, onToggle, testid }) => {
    const list = flavors.filter((f) => f.product_type === productType);
    if (list.length === 0) return null;
    return (
        <div data-testid={testid}>
            <p className={labelCls}>{label} (optional — pick any)</p>
            <div className="flex flex-wrap gap-2">
                {list.map((f) => {
                    const on = selected.includes(f.name);
                    return (
                        <button
                            type="button"
                            key={f.id}
                            onClick={() => onToggle(productType, f.name)}
                            data-testid={`${testid}-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`rounded-full px-4 py-2 text-xs font-bold tracking-wide transition-colors duration-300 active:scale-95 ${
                                on ? "bg-pink text-ink" : "border border-zinc-300 text-zinc-600 hover:border-pink"
                            }`}
                        >
                            {f.name}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const ChoiceCard = ({ selected, onClick, title, copy, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`rounded-2xl border-2 p-5 text-left transition-all duration-300 active:scale-95 ${
            selected ? "border-pink bg-pink-soft" : "border-zinc-200 bg-white hover:border-pink"
        }`}
    >
        <p className="font-oswald uppercase text-lg">{title}</p>
        {copy && <p className="text-xs text-zinc-500 mt-1">{copy}</p>}
    </button>
);

export const BookPage = () => {
    usePageMeta("Book The Show — Cotton Candy & Popcorn Catering NYC | Showtime Carnival Co.",
        "Request cotton candy and popcorn catering for your NYC event. Parties, corporate, schools, weddings — tell us about the gig.");
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(INITIAL);
    const [flavors, setFlavors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(null);

    useEffect(() => {
        api.get("/flavors").then((res) => setFlavors(res.data || [])).catch(() => {});
    }, []);

    const toggleFlavor = (productType, name) => {
        const key = productType === "cotton_candy" ? "flavor_cotton_candy" : "flavor_popcorn";
        setForm((f) => ({
            ...f,
            [key]: f[key].includes(name) ? f[key].filter((n) => n !== name) : [...f[key], name],
        }));
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const stepValid = () => {
        if (step === 0) return form.event_type && form.event_date && form.start_time && form.duration && form.guests && form.address;
        if (step === 1) return !!form.treats;
        if (step === 2) return form.name && form.email && form.phone;
        return true;
    };

    const next = () => {
        if (!stepValid()) {
            toast.error("Fill in the highlighted fields to continue.");
            return;
        }
        setStep((s) => Math.min(s + 1, 3));
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await api.post("/booking-requests", { ...form, guests: parseInt(form.guests, 10) });
            setConfirmed(res.data.request_number);
            window.scrollTo(0, 0);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    if (confirmed) {
        return (
            <section data-testid="booking-confirmation" className="min-h-[100svh] bg-ink tent-stripes text-white flex items-center">
                <div className="mx-auto max-w-2xl px-5 py-40 text-center">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }}>
                        <CheckCircle2 size={64} className="mx-auto text-pink mb-8" />
                    </motion.div>
                    <h1 className="font-oswald uppercase text-5xl md:text-7xl leading-[0.9]">
                        Your Show Is<br /><span className="text-pink">In The Works.</span>
                    </h1>
                    <p className="mt-8 text-zinc-300 text-base md:text-lg leading-relaxed">
                        We received your event request. Showtime will review the details and contact you to confirm availability and next steps.
                    </p>
                    <div className="mt-10 inline-block rounded-full border-2 border-pink px-8 py-4">
                        <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-zinc-400 mb-1">Request Number</p>
                        <p className="font-oswald text-3xl text-pink" data-testid="booking-request-number">{confirmed}</p>
                    </div>
                    <p className="mt-8 text-sm text-zinc-500">Keep this number handy — it references your request.</p>
                </div>
            </section>
        );
    }

    return (
        <>
            <PageHero
                kicker="Book The Show"
                title="Your Event. Our Show."
                copy="This is a request to book — not an instant reservation. We review every request personally and confirm availability with you."
                testid="book-hero"
            />
            <section className="bg-paper py-16 md:py-24">
                <div className="mx-auto max-w-3xl px-5 md:px-10">
                    <div className="flex gap-2 mb-10" data-testid="booking-steps">
                        {STEPS.map((s, i) => (
                            <div key={s} className="flex-1">
                                <div className={`h-1.5 rounded-full mb-2 transition-colors duration-500 ${i <= step ? "bg-pink" : "bg-zinc-200"}`} />
                                <p className={`text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase ${i <= step ? "text-ink" : "text-zinc-400"}`}>
                                    {i + 1}. {s}
                                </p>
                            </div>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={step}
                            initial={{ opacity: 0, x: 32 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -32 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="bg-white rounded-3xl border border-zinc-200 p-6 md:p-10"
                            data-testid={`booking-step-${step}`}
                        >
                            {step === 0 && (
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div className="sm:col-span-2">
                                        <label htmlFor="bk-type" className={labelCls}>Event Type</label>
                                        <select id="bk-type" value={form.event_type} onChange={set("event_type")} className={inputCls} data-testid="booking-type-select">
                                            {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="bk-date" className={labelCls}>Requested Date *</label>
                                        <input id="bk-date" type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} data-testid="booking-date-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="bk-time" className={labelCls}>Start Time *</label>
                                        <input id="bk-time" type="time" value={form.start_time} onChange={set("start_time")} className={inputCls} data-testid="booking-time-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="bk-duration" className={labelCls}>Approx. Duration *</label>
                                        <select id="bk-duration" value={form.duration} onChange={set("duration")} className={inputCls} data-testid="booking-duration-select">
                                            <option value="">Select...</option>
                                            {["1 hour", "2 hours", "3 hours", "4 hours", "5+ hours"].map((d) => <option key={d}>{d}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="bk-guests" className={labelCls}>Number of Guests *</label>
                                        <input id="bk-guests" type="number" min="1" value={form.guests} onChange={set("guests")} placeholder="50" className={inputCls} data-testid="booking-guests-input" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="bk-address" className={labelCls}>Event Address / Location *</label>
                                        <input id="bk-address" value={form.address} onChange={set("address")} placeholder="Street, neighborhood, city" className={inputCls} data-testid="booking-address-input" />
                                    </div>
                                    <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                                        <ChoiceCard selected={form.setting === "indoor"} onClick={() => set("setting")("indoor")} title="Indoor" testid="booking-indoor" />
                                        <ChoiceCard selected={form.setting === "outdoor"} onClick={() => set("setting")("outdoor")} title="Outdoor" testid="booking-outdoor" />
                                    </div>
                                </div>
                            )}
                            {step === 1 && (
                                <div className="grid gap-5">
                                    <div>
                                        <p className={labelCls}>What's on the bill? *</p>
                                        <div className="grid sm:grid-cols-3 gap-3">
                                            <ChoiceCard selected={form.treats === "cotton_candy"} onClick={() => set("treats")("cotton_candy")} title="Cotton Candy" copy="Fresh spun on-site" testid="booking-treat-cotton-candy" />
                                            <ChoiceCard selected={form.treats === "popcorn"} onClick={() => set("treats")("popcorn")} title="Popcorn" copy="Fresh popped on-site" testid="booking-treat-popcorn" />
                                            <ChoiceCard selected={form.treats === "both"} onClick={() => set("treats")("both")} title="Both" copy="The full show" testid="booking-treat-both" />
                                        </div>
                                    </div>
                                    {(form.treats === "cotton_candy" || form.treats === "both") && (
                                        <FlavorChips productType="cotton_candy" label="Cotton candy flavors" flavors={flavors} selected={form.flavor_cotton_candy} onToggle={toggleFlavor} testid="booking-flavors-cc" />
                                    )}
                                    {(form.treats === "popcorn" || form.treats === "both") && (
                                        <FlavorChips productType="popcorn" label="Popcorn flavors" flavors={flavors} selected={form.flavor_popcorn} onToggle={toggleFlavor} testid="booking-flavors-pc" />
                                    )}
                                    <div>
                                        <label htmlFor="bk-addons" className={labelCls}>Flavor requests / special requests (optional)</label>
                                        <textarea id="bk-addons" rows={3} value={form.add_ons} onChange={set("add_ons")} placeholder="Tell us about flavors, Mystery Flavors, event theme, setup needs or anything else we should know..." className={inputCls} data-testid="booking-addons-input" />
                                    </div>
                                </div>
                            )}
                            {step === 2 && (
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="bk-name" className={labelCls}>Your Name *</label>
                                        <input id="bk-name" value={form.name} onChange={set("name")} placeholder="Jamie Rivera" className={inputCls} data-testid="booking-name-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="bk-company" className={labelCls}>Company / Organization</label>
                                        <input id="bk-company" value={form.company} onChange={set("company")} placeholder="Optional" className={inputCls} data-testid="booking-company-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="bk-email" className={labelCls}>Email *</label>
                                        <input id="bk-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="booking-email-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="bk-phone" className={labelCls}>Phone *</label>
                                        <input id="bk-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(718) 555-0134" className={inputCls} data-testid="booking-phone-input" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="bk-notes" className={labelCls}>Special requests / notes</label>
                                        <textarea id="bk-notes" rows={4} value={form.notes} onChange={set("notes")} placeholder="Anything else we should know..." className={inputCls} data-testid="booking-notes-input" />
                                    </div>
                                </div>
                            )}
                            {step === 3 && (
                                <div data-testid="booking-review">
                                    <h3 className="font-oswald uppercase text-2xl mb-6">Review Your Request</h3>
                                    <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                                        {[
                                            ["Event", form.event_type],
                                            ["Date & Time", formatEventDateTime(form.event_date, form.start_time)],
                                            ["Duration", form.duration],
                                            ["Guests", form.guests],
                                            ["Location", form.address],
                                            ["Setting", form.setting],
                                            ["Treats", { cotton_candy: "Cotton Candy", popcorn: "Popcorn", both: "Cotton Candy + Popcorn" }[form.treats]],
                                            ...(form.flavor_cotton_candy.length ? [["Cotton Candy Flavors", form.flavor_cotton_candy.join(", ")]] : []),
                                            ...(form.flavor_popcorn.length ? [["Popcorn Flavors", form.flavor_popcorn.join(", ")]] : []),
                                            ["Name", form.name],
                                            ["Email", form.email],
                                            ["Phone", form.phone],
                                            ["Company", form.company || "—"],
                                        ].map(([k, v]) => (
                                            <div key={k}>
                                                <dt className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-400">{k}</dt>
                                                <dd className="font-semibold mt-0.5">{v}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                    {form.notes && <p className="mt-4 text-sm text-zinc-500">Notes: {form.notes}</p>}
                                    <p className="mt-8 text-xs text-zinc-400 leading-relaxed">
                                        Submitting sends a request to book — it does not lock your date. Showtime confirms availability and next steps with you directly.
                                    </p>
                                </div>
                            )}

                            <div className="mt-10 flex items-center justify-between gap-4">
                                <button
                                    type="button"
                                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                                    disabled={step === 0}
                                    data-testid="booking-back-button"
                                    className="inline-flex items-center gap-2 rounded-full border-2 border-zinc-300 px-6 py-3.5 font-oswald uppercase tracking-widest text-sm text-zinc-600 hover:border-ink disabled:opacity-30 transition-colors"
                                >
                                    <ArrowLeft size={15} /> Back
                                </button>
                                {step < 3 ? (
                                    <button
                                        type="button"
                                        onClick={next}
                                        data-testid="booking-next-button"
                                        className="inline-flex items-center gap-2 rounded-full bg-ink text-white px-8 py-3.5 font-oswald uppercase tracking-widest text-sm hover:bg-pink hover:text-ink transition-colors duration-300 active:scale-95"
                                    >
                                        Next <ArrowRight size={15} />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={submit}
                                        disabled={loading}
                                        data-testid="booking-submit-button"
                                        className="inline-flex items-center gap-2 rounded-full bg-pink px-8 py-3.5 font-oswald uppercase tracking-widest text-sm text-ink hover:bg-ink hover:text-white transition-colors duration-300 active:scale-95 disabled:opacity-50"
                                    >
                                        {loading ? "Sending..." : "Send Request"} <ArrowRight size={15} />
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>
        </>
    );
};
